const GTMActionType = {
    FORM_START: 'form_start',
    FORM_ERROR: 'form_error',
    FORM_STEP_COMPLETE: 'form_step_complete',
    FORM_COMPLETE: 'form_complete',
    SEARCH_START: 'view_search_results',
    SEARCH_RESULT_CLICK: 'click_search_results',
    BUY_TICKETS: 'buy_tickets',
    CALENDAR_EVENT: 'calendar_event',
    CLICK_FILTER: 'click_filter',
    CLICK_STADIUM_PIN: 'click_stadium_pin',
    PIN_STADIUM_PIN: 'stadium_pin',
} as const;
type GTMActionType = typeof GTMActionType[keyof typeof GTMActionType];

type PushGTMEventStepProps = { step_name: string; step_number: number; total_steps: number };
type PushGTMEventNameTypeProps = { name: string; type: string };
type PushGTMEventSearchProps = { search_term: string; search_type: string; search_subtype: string; filter: string; num_results: number };
type PushGTMEventCalendarProps = { ev_name: string; ev_type: string; ev_date: string; link_url: string };
type PushGTMFilterProps = { event: 'select' | 'remove'; search_term: string; search_type: string; search_subtype: string; filter: string };
type PushGTMEventClickStadiumPinProps = { section: string; name: string; actionType: typeof GTMActionType.CLICK_STADIUM_PIN };
type PushGTMEventPinStadiumPinProps = { section: string; type: string; actionType: typeof GTMActionType.PIN_STADIUM_PIN };
type PushGTMEventProps = PushGTMEventNameTypeProps & { actionType: typeof GTMActionType.FORM_START | typeof GTMActionType.FORM_COMPLETE }
    | PushGTMEventNameTypeProps & PushGTMEventStepProps & { message: string; actionType: typeof GTMActionType.FORM_ERROR }
    | PushGTMEventNameTypeProps & PushGTMEventStepProps & { actionType: typeof GTMActionType.FORM_STEP_COMPLETE }
    | PushGTMEventSearchProps & { actionType: typeof GTMActionType.SEARCH_START }
    | PushGTMEventSearchProps & { position: number; result: string; result_type: string; actionType: typeof GTMActionType.SEARCH_RESULT_CLICK }
    | PushGTMEventCalendarProps & { actionType: typeof GTMActionType.CALENDAR_EVENT }
    | PushGTMFilterProps & { actionType: typeof GTMActionType.CLICK_FILTER }
    | PushGTMEventClickStadiumPinProps & { actionType: typeof GTMActionType.CLICK_STADIUM_PIN }
    | PushGTMEventPinStadiumPinProps & { actionType: typeof GTMActionType.PIN_STADIUM_PIN }
    | { ev_name: string; ev_date: string; actionType: typeof GTMActionType.BUY_TICKETS };

export const pushGTMEvent = ({ ...props }: PushGTMEventProps): void => {
    if (!Array.isArray(window.dataLayer)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).dataLayer = [];
    }

    if (props.actionType === GTMActionType.FORM_START || props.actionType === GTMActionType.FORM_COMPLETE) {
        const eventName = props.actionType === GTMActionType.FORM_START ? 'custom.form.start' : 'custom.form.complete';
        window.dataLayer.push({
            event: eventName,
            name: props.name,
            type: props.type,
            action: props.actionType,
        });
    } else if (props.actionType === GTMActionType.FORM_ERROR) {
        window.dataLayer.push({
            event: 'custom.form.error',
            name: props.name,
            type: props.type,
            action: props.actionType,
            step_name: props.step_name,
            step_number: props.step_number,
            total_steps: props.total_steps,
            message: props.message,
        });
    } else if (props.actionType === GTMActionType.FORM_STEP_COMPLETE) {
        window.dataLayer.push({
            event: 'custom.form.step_complete',
            name: props.name,
            type: props.type,
            step_name: props.step_name,
            step_number: props.step_number,
            total_steps: props.total_steps,
            action: props.actionType,
        });
    } else if (props.actionType === GTMActionType.SEARCH_START) {
        window.dataLayer.push({
            event: 'custom.search.start',
            action: props.actionType,
            search_term: props.search_term,
            search_type: props.search_type,
            search_subtype: props.search_subtype,
            filter: props.filter,
            num_results: props.num_results,
        });
    } else if (props.actionType === GTMActionType.SEARCH_RESULT_CLICK) {
        window.dataLayer.push({
            event: 'custom.search.clicked',
            action: props.actionType,
            search_term: props.search_term,
            search_type: props.search_type,
            filter: props.filter,
            num_results: props.num_results,
            search_subtype: props.search_subtype,
            result: props.result,
            position: props.position,
            result_type: props.result_type,
        });
    } else if (props.actionType === GTMActionType.BUY_TICKETS) {
        window.dataLayer.push({
            event: 'custom.buy.tickets',
            ev_name: props.ev_name,
            ev_date: props.ev_date,
        });
    } else if (props.actionType === GTMActionType.CALENDAR_EVENT) {
        window.dataLayer.push({
            event: 'custom.calendar_event',
            ev_name: props.ev_name,
            ev_type: props.ev_type,
            cal_evt_date: props.ev_date,
            link_url: props.link_url,
        });
    } else if (props.actionType === GTMActionType.CLICK_FILTER) {
        const clickEventName = props.event === 'remove' ? 'custom.filter.removed' : 'custom.filter.selected';
        window.dataLayer.push({
            event: clickEventName,
            action: 'click_filter',
            search_term: props.search_term,
            search_type: props.search_type,
            search_subtype: props.search_subtype,
            filter: props.filter,
        });
    } else if (props.actionType === GTMActionType.CLICK_STADIUM_PIN) {
        window.dataLayer.push({
            event: 'custom.stadium.search.pin',
            section: props.section,
            name: props.name,
            action: 'stadium_marker',
        });
    } else if (props.actionType === GTMActionType.PIN_STADIUM_PIN) {
        window.dataLayer.push({
            event: 'custom.stadium.search.pin',
            section: props.section,
            type: props.type,
            action: 'stadium_pin',
        });
    }
};
